import { useState } from 'react'
import {
  ArrowUpRight,
  Check,
  FileText,
  Link2,
  Plus,
  Scissors,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import { CARD_COLORS, CARD_COLUMNS } from '../data'
import { charCount, columnMeta, relativeTime } from '../lib/utils'

export default function Inspector({
  card,
  project,
  onClose,
  onPatch,
  onDelete,
  onSplit,
  onOpenDocument,
  onOpenRoundtable,
  onAddConnection,
  onRemoveConnection,
  onSelectCard,
}) {
  const [tagInput, setTagInput] = useState('')
  const [addingTag, setAddingTag] = useState(false)
  const [targetId, setTargetId] = useState('')
  if (!card) return null
  const index = project.cards.findIndex((item) => item.id === card.id)
  const status = columnMeta(card.column)
  const links = project.connections.filter((link) => link.source === card.id || link.target === card.id)
  const related = links.map((link) => {
    const otherId = link.source === card.id ? link.target : link.source
    const other = project.cards.find((item) => item.id === otherId)
    return other ? { link, card: other } : null
  }).filter(Boolean)
  const available = project.cards.filter((item) => item.id !== card.id && !related.some((entry) => entry.card.id === item.id))

  function addTag() {
    const value = tagInput.trim().replace(/^#/, '')
    if (!value) {
      setAddingTag(false)
      return
    }
    if (!(card.tags || []).includes(value)) onPatch({ tags: [...(card.tags || []), value] })
    setTagInput('')
    setAddingTag(false)
  }

  function addRelation() {
    if (!targetId) return
    onAddConnection(card.id, targetId)
    setTargetId('')
  }

  return (
    <aside className="inspector" aria-label="卡片详情">
      <div className="inspector-head">
        <div>
          <div className="inspector-eyebrow">CARD DETAIL</div>
          <h2>卡片详情</h2>
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="关闭卡片详情"><X size={18} /></button>
      </div>

      <div className="inspector-scroll">
        <div className="inspector-index">
          <span className="inspector-index__number">{String(index + 1).padStart(2, '0')}</span>
          <span className="inspector-index__act">{card.act}</span>
          <span className="inspector-index__meta">{charCount(card.content)} 字</span>
        </div>

        <input
          className="inspector-title"
          value={card.title}
          onChange={(event) => onPatch({ title: event.target.value })}
          aria-label="卡片标题"
        />

        <div className="inspector-stage">
          {CARD_COLUMNS.map((column) => (
            <button
              key={column.id}
              className={card.column === column.id ? 'is-active' : ''}
              onClick={() => onPatch({ column: column.id })}
              style={{ '--stage': column.color }}
            >
              <i style={{ background: column.color }} />
              {column.label}
              {card.column === column.id ? <Check size={11} /> : null}
            </button>
          ))}
        </div>

        <div className="inspector-summary">
          <span>卡片提要</span>
          <p>{card.summary}</p>
        </div>

        <section className="inspector-section">
          <div className="inspector-section__head">
            <h3>标签</h3>
            <button className="mini-text-btn" onClick={() => setAddingTag(true)}><Plus size={12} /> 添加</button>
          </div>
          <div className="inspector-tags">
            {(card.tags || []).map((tag) => (
              <span className="editable-tag" key={tag}>
                {tag}
                <button
                  aria-label={'删除标签 ' + tag}
                  onClick={() => onPatch({ tags: (card.tags || []).filter((item) => item !== tag) })}
                ><X size={10} /></button>
              </span>
            ))}
            {addingTag ? (
              <input
                className="tag-input"
                autoFocus
                value={tagInput}
                onChange={(event) => setTagInput(event.target.value)}
                onBlur={addTag}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') { event.preventDefault(); addTag() }
                  if (event.key === 'Escape') { setAddingTag(false); setTagInput('') }
                }}
                placeholder="标签名"
              />
            ) : null}
            {!(card.tags || []).length && !addingTag ? <span className="inspector-muted">还没有标签</span> : null}
          </div>
        </section>

        <section className="inspector-section">
          <div className="inspector-section__head">
            <h3>纸张</h3>
            <span className="inspector-muted">{status.label}阶段</span>
          </div>
          <div className="color-row">
            {Object.entries(CARD_COLORS).map(([key, color]) => (
              <button
                key={key}
                className={'color-swatch ' + (card.color === key ? 'is-active' : '')}
                style={{ background: color }}
                onClick={() => onPatch({ color: key })}
                aria-label={'纸张颜色 ' + key}
              >
                {card.color === key ? <Check size={11} /> : null}
              </button>
            ))}
          </div>
        </section>

        <section className="inspector-section">
          <div className="inspector-section__head">
            <h3>关联 <span className="section-count">{related.length}</span></h3>
            <span className="inspector-muted">看板上的线</span>
          </div>
          <div className="inspector-links">
            {related.map(({ link, card: other }) => (
              <div className="inspector-link" key={link.id}>
                <span className="inspector-link__dot" style={{ background: CARD_COLORS[other.color] || CARD_COLORS.paper }} />
                <button onClick={() => onSelectCard(other.id)}>
                  <em>{link.label || '相关'}</em>
                  <strong>{other.title}</strong>
                </button>
                <button className="relation-remove" onClick={() => onRemoveConnection(link.id)} aria-label="移除关联"><X size={12} /></button>
              </div>
            ))}
            {!related.length ? <p className="inspector-muted-line">这张卡片还没有接入任何关系。去关联视图连一条线吧。</p> : null}
          </div>
          {available.length ? (
            <div className="add-relation">
              <select value={targetId} onChange={(event) => setTargetId(event.target.value)} aria-label="选择要关联的卡片">
                <option value="">选择一张卡片…</option>
                {available.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
              </select>
              <button onClick={addRelation} disabled={!targetId} aria-label="建立关联"><Link2 size={14} /></button>
            </div>
          ) : null}
        </section>

        <section className="inspector-section">
          <div className="inspector-section__head">
            <h3>批注</h3>
            <span className="inspector-muted">给未来的自己</span>
          </div>
          <textarea
            className="notes-box"
            value={card.notes || ''}
            onChange={(event) => onPatch({ notes: event.target.value })}
            placeholder="这里可以收下 AI 的建议，或留下一句提醒。"
            rows={4}
          />
        </section>

        <div className="inspector-date">最后编辑于 {relativeTime(card.updatedAt)}</div>
      </div>

      <div className="inspector-actions">
        <button className="btn btn--outline btn--full" onClick={() => onOpenDocument(card.id)}>
          <FileText size={15} /> 打开文稿
        </button>
        <button className="btn btn--outline btn--full" onClick={() => onSplit(card.id)}>
          <Scissors size={15} /> 拆分卡片
        </button>
        <button className="inspector-ai" onClick={() => onOpenRoundtable([card.id])}>
          <Sparkles size={15} /> 带到 AI 圆桌
          <ArrowUpRight size={14} />
        </button>
        <div className="inspector-bottom-row">
          <span>多选两张卡片可合并</span>
          <button className="danger-link" onClick={() => onDelete(card.id)}><Trash2 size={13} /> 删除</button>
        </div>
      </div>
    </aside>
  )
}
